import { useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { Page } from '../../app/AppShell.js';
import { Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../../components/ui.js';
import { FOLDER_SCOPABLE_PERMISSIONS, type ConsoleUser, type FolderNode } from '../../lib/types.js';

/**
 * The shared media/layout folder tree, plus per-user folder access grants
 * (ADR-0024). A grant is always additive to the user's tenant-wide role.
 */
export function FoldersScreen(): ReactNode {
  const folders = useQuery(() => api.folders(), []);
  const toast = useToast();
  const [creating, setCreating] = useState<FolderNode | null | undefined>(undefined);
  const [renaming, setRenaming] = useState<FolderNode | null>(null);
  const [managing, setManaging] = useState<FolderNode | null>(null);

  async function remove(f: FolderNode): Promise<void> {
    if (!confirm(`Delete folder "${f.name}"?`)) return;
    try {
      await api.deleteFolder(f.id);
      folders.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  async function move(f: FolderNode, parentId: string | null): Promise<void> {
    if (parentId === f.parentId) return;
    try {
      await api.moveFolder(f.id, parentId);
      folders.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  const all = folders.data ?? [];
  const byParent = new Map<string | null, FolderNode[]>();
  for (const f of all) {
    const key = f.parentId;
    const list = byParent.get(key) ?? [];
    list.push(f);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));

  function renderLevel(parentId: string | null, depth: number): ReactNode[] {
    return (byParent.get(parentId) ?? []).flatMap((f) => [
      <tr key={f.id}>
        <td style={{ paddingLeft: depth * 20 }}>{f.name}</td>
        <td className="muted small">
          {f.mediaCount} media · {f.layoutsCount} layouts · {f.childrenCount} subfolder{f.childrenCount === 1 ? '' : 's'}
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="row">
            <select
              value={f.parentId ?? ''}
              onChange={(e) => void move(f, e.target.value || null)}
              title="Move to a different parent folder"
            >
              <option value="">(root)</option>
              {all
                .filter((o) => o.id !== f.id && !o.path.includes(`/${f.id}/`))
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </select>
            <button className="btn small" onClick={() => setRenaming(f)}>
              Rename
            </button>
            <button className="btn small" onClick={() => setCreating(f)} title="New subfolder here">
              + Subfolder
            </button>
            <button className="btn small" onClick={() => setManaging(f)}>
              Access
            </button>
            <button className="btn small danger" onClick={() => void remove(f)}>
              Delete
            </button>
          </div>
        </td>
      </tr>,
      ...renderLevel(f.id, depth + 1),
    ]);
  }

  return (
    <Page
      title="Folders"
      actions={
        <button className="btn primary" onClick={() => setCreating(null)}>
          New folder
        </button>
      }
    >
      <p className="muted small" style={{ marginTop: 0 }}>
        Organise media and layouts into folders, then grant a specific user extra,
        folder-scoped permissions — on top of whatever their role already gives them.
      </p>

      {folders.loading && !folders.data ? <Loading what="folders" /> : null}
      {folders.error ? <ErrorNote error={folders.error} /> : null}

      {folders.data ? (
        folders.data.length === 0 ? (
          <EmptyState>No folders yet — media and layouts are unfiled by default.</EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contents</th>
                  <th />
                </tr>
              </thead>
              <tbody>{renderLevel(null, 0)}</tbody>
            </table>
          </div>
        )
      ) : null}

      {creating !== undefined ? (
        <CreateFolderModal
          parent={creating}
          onClose={() => setCreating(undefined)}
          onCreated={() => {
            setCreating(undefined);
            folders.refetch();
          }}
        />
      ) : null}

      {renaming ? (
        <RenameFolderModal
          folder={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={() => {
            setRenaming(null);
            folders.refetch();
          }}
        />
      ) : null}

      {managing ? <FolderAccessModal folder={managing} onClose={() => setManaging(null)} /> : null}
    </Page>
  );
}

function CreateFolderModal({
  parent,
  onClose,
  onCreated,
}: {
  parent: FolderNode | null;
  onClose: () => void;
  onCreated: () => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      await api.createFolder(name.trim(), parent?.id ?? null);
      onCreated();
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Modal title={parent ? `New subfolder in “${parent.name}”` : 'New folder'} onClose={onClose}>
      <div className="stack">
        <label className="field">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || name.trim() === ''} onClick={() => void submit()}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RenameFolderModal({
  folder,
  onClose,
  onRenamed,
}: {
  folder: FolderNode;
  onClose: () => void;
  onRenamed: () => void;
}): ReactNode {
  const [name, setName] = useState(folder.name);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      await api.renameFolder(folder.id, name.trim());
      onRenamed();
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Modal title="Rename folder" onClose={onClose}>
      <div className="stack">
        <label className="field">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy || name.trim() === ''} onClick={() => void submit()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function FolderAccessModal({ folder, onClose }: { folder: FolderNode; onClose: () => void }): ReactNode {
  const grants = useQuery(() => api.folderGrants(folder.id), [folder.id]);
  const users = useQuery(() => api.users(), []);
  const toast = useToast();
  const [userId, setUserId] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const grantedUserIds = new Set((grants.data ?? []).map((g) => g.userId));
  const candidates = (users.data ?? []).filter(
    (u: ConsoleUser) => !grantedUserIds.has(Number(u.id)),
  );

  function toggle(slug: string): void {
    setPermissions((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));
  }

  async function add(): Promise<void> {
    setBusy(true);
    try {
      await api.createFolderGrant(folder.id, { userId: Number(userId), permissions });
      setUserId('');
      setPermissions([]);
      grants.refetch();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  async function remove(grantId: string): Promise<void> {
    try {
      await api.deleteFolderGrant(folder.id, grantId);
      grants.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Modal title={`Access — “${folder.name}”`} onClose={onClose} wide>
      <p className="muted small" style={{ marginTop: 0 }}>
        Grants here add reach into this folder and everything under it — on top of, never
        instead of, what the user's role already allows.
      </p>

      {grants.loading && !grants.data ? <Loading what="grants" /> : null}
      {grants.error ? <ErrorNote error={grants.error} /> : null}

      {grants.data && grants.data.length > 0 ? (
        <table className="grid" style={{ marginBottom: 16 }}>
          <thead>
            <tr>
              <th>User</th>
              <th>Permissions</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {grants.data.map((g) => (
              <tr key={g.id}>
                <td>
                  {g.userName}
                  <div className="faint small">{g.userEmail}</div>
                </td>
                <td className="mono small">{g.permissions.join(', ')}</td>
                <td>
                  <button className="btn small danger" onClick={() => void remove(g.id)}>
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState>No one has folder-scoped access here yet.</EmptyState>
      )}

      <div className="stack" style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
        <label className="field">
          User
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Select a user…</option>
            {candidates.map((u: ConsoleUser) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          Permissions
          <div className="row" style={{ flexWrap: 'wrap' }}>
            {FOLDER_SCOPABLE_PERMISSIONS.map((slug) => (
              <label key={slug} className="row" style={{ gap: 4, marginRight: 12 }}>
                <input type="checkbox" checked={permissions.includes(slug)} onChange={() => toggle(slug)} />
                <span className="mono small">{slug}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" disabled={busy || userId === '' || permissions.length === 0} onClick={() => void add()}>
            {busy ? 'Granting…' : 'Grant access'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
